import { useEffect, useState } from "react";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";

type SettingsKey =
  | "noticePeriodHours"
  | "minTripMiles"
  | "maxTripMiles"
  | "operatingHoursFrom"
  | "operatingHoursTo"
  | "maxVehicles";

type Props = {
  title: string;
  fields: { name: SettingsKey; label: string; type?: string }[];
};

export default function CompanySettingsPage({ title, fields }: Props) {
  const [form, setForm] = useState<Record<string, string | number | null>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    axiosInstance.get("/company-settings").then((res) => {
      const d = res.data;
      const slice: Record<string, string | number | null> = {};
      fields.forEach((f) => {
        slice[f.name] = d[f.name] ?? "";
      });
      setForm(slice);
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const current = (await axiosInstance.get("/company-settings")).data;
      await axiosInstance.put("/company-settings", {
        ...current,
        ...Object.fromEntries(
          fields.map((f) => [
            f.name,
            f.type === "number"
              ? form[f.name] === "" || form[f.name] === null
                ? null
                : Number(form[f.name])
              : form[f.name] || null,
          ])
        ),
      });
      setMessage("Saved successfully");
    } catch {
      setMessage("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle={title} />
      <form onSubmit={handleSave} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 max-w-lg space-y-4">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          fields.map((f) => (
            <div key={f.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
              <input
                type={f.type || "text"}
                value={form[f.name] ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    [f.name]: e.target.value,
                  }))
                }
                className="w-full rounded-lg border px-4 py-2"
              />
            </div>
          ))
        )}
        {message && <p className="text-sm text-green-600">{message}</p>}
        <button type="submit" disabled={saving || loading} className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? "Saving..." : "Save"}
        </button>
      </form>
    </div>
  );
}
